import { Injectable } from '@angular/core'
import { SearchDataService } from './search-data.service'
import { map, switchMap } from 'rxjs/operators'
import { buildFacetFilters, mapSearchResponseToCategoryView } from './category.service.utility'
import { FacetFilter, SearchResponseDataCategories } from './category.service.interface'
import { SearchQueryParams } from './search-data.service.interface'
import { combineLatest, forkJoin, Observable } from 'rxjs'
import { VerticalService } from './vertical.service'
import { CategoryView } from '../libs/category-list/category-list.interface'
import { SiteIds } from '../shared/models/site.model'

export const VIEW_ALL_PER_PAGE_COUNT = 102
export const DEFAULT_TEAMS_PER_FACET = 12

export const SITE_TEAM_FACETS_DICT: Record<number, FacetFilter[]> = {
  [SiteIds.FLOHOOPS]: [FacetFilter.Conference, FacetFilter.Gender],
  [SiteIds.FLOSOFTBALL]: [FacetFilter.Conference],
  [SiteIds.FLOHOCKEY]: [FacetFilter.League],
  [SiteIds.FLOFOOTBALL]: [FacetFilter.GoverningBody],
  [SiteIds.FLOBASEBALL]: [FacetFilter.GoverningBody]
}

@Injectable({
  providedIn: 'root'
})
export class CategoryService {
  constructor(private readonly searchDataService: SearchDataService, private readonly vs: VerticalService) {}

  public searchAthletes$ = (
    params?: SearchQueryParams,
    useOfp = false,
    page = 1
  ): Observable<readonly CategoryView[]> => {
    return this.searchDataService
      .searchCategories({
        type: 'person',
        sort: 'title',
        limit: VIEW_ALL_PER_PAGE_COUNT.toString(),
        page: page.toString(),
        useOfp,
        ...params
      })
      .pipe(map(response => mapSearchResponseToCategoryView([{ response }])))
  }

  public searchTeams$ = (
    params?: SearchQueryParams,
    limitPer = DEFAULT_TEAMS_PER_FACET,
    page = 1
  ): Observable<readonly CategoryView[]> => {
    const searchParams: SearchQueryParams = {
      type: 'team',
      sort: 'title',
      limit: limitPer.toString(),
      page: page.toString(),
      ...params
    }
    // if searching for a specific team, no facets are needed so we eject here with the search result
    if ((params?.q && params.q.length) || params?.facets) {
      return forkJoin([this.searchAndPreserveFacets$(searchParams)]).pipe(
        map(result => mapSearchResponseToCategoryView(result))
      )
    }

    // TODO: nuke combineLatest. View All state doesn't need the first this.searchDataService call
    return combineLatest([
      this.searchDataService.searchCategories({ ...searchParams, metadata_only: 1 }),
      this.vs.siteId$
    ]).pipe(
      map(([response, siteId]) => {
        const facets = response.meta.extra[0].facets
        return siteId && SITE_TEAM_FACETS_DICT[siteId] ? buildFacetFilters(facets, SITE_TEAM_FACETS_DICT[siteId]) : []
      }),
      switchMap(facetFilters => {
        const facetParams: SearchQueryParams = {
          limit: limitPer.toString(),
          page: page.toString(),
          ...searchParams
        }

        // View All if no facet (Athletes) or if only a single facet
        if (facetFilters.length < 2) {
          facetParams.limit = VIEW_ALL_PER_PAGE_COUNT.toString()
          return forkJoin([this.searchAndPreserveFacets$(facetParams)])
        }

        // Grouped by Facet
        return forkJoin([...facetFilters.map(facetFilter => this.searchAndPreserveFacets$(facetParams, facetFilter))])
      }),
      map(results => mapSearchResponseToCategoryView(results))
    )
  }

  private searchAndPreserveFacets$ = (
    params: SearchQueryParams,
    facetFilter?: Record<FacetFilter, string>
  ): Observable<SearchResponseDataCategories> => {
    const stringifiedFacetFilter = JSON.stringify(facetFilter)
    const formattedParams = facetFilter ? { facets: stringifiedFacetFilter, ...params } : params

    return this.searchDataService.searchCategories(formattedParams).pipe(
      map(response => {
        return {
          response,
          queryParams: formattedParams
        }
      })
    )
  }
}
